import { type FC, Fragment, useEffect, useState } from 'react';

import { PROPERTY_LEVELS } from '@/constants/property-level';
import { getProjects, type Projects } from '@/features/projects/api/projects';
import { useInfiniteScroll } from '@/hooks/use-infinite-scroll';
import { getRenderValue } from '@/utils/getEmptyVisualValue';
import { getFinalAreaRoom } from '@/utils/getFinalAreaRoom';
import { getFinalLivingArea } from '@/utils/getFinalLivingArea';
import { getFinalPrice } from '@/utils/getFinalPrice';

import { type Estates, getEstates } from '../api/estates';
import type { EstateCardProps } from './estate-grid-card';

type EstateAdditionalExtendedInfoProps = {
  creator: string;
  createdAt: string;
};

export type EstateLongCardProps = EstateCardProps &
  EstateAdditionalExtendedInfoProps & { thumbnailId: string };

interface EstatesInfiniteScrollProps {
  Card: FC<EstateLongCardProps>;
  filters?: object;
  filterOwn?: boolean;
  sort?: {
    by?: 'name' | 'rooms' | 'area' | 'price' | 'createdAt' | 'updatedAt' | 'creator';
    order?: 'asc' | 'desc';
  };
  type?: 'estate' | 'project';
  cardProps?: Pick<EstateLongCardProps, 'btnActionRender'> & {
    showBuilding?: boolean;
  };
}

interface EstatesState {
  [key: string]: Estates[number] | Projects[number];
}

export const EstatesInfiniteScroll: FC<EstatesInfiniteScrollProps> = ({
  Card,
  filters,
  filterOwn,
  sort,
  type = 'estate',
  cardProps = {},
}) => {
  const [loading, setLoading] = useState(false);
  const [stopLoad, setStopLoad] = useState(false);
  const [estates, setEstates] = useState<EstatesState | null>(null);

  const isNotEmptyFilters = filters && Object.keys(filters).length;

  const { lastElementRef, pageNum, resetPageNum } = useInfiniteScroll<HTMLDivElement>({
    loading,
    stopLoad,
    startPage: 2,
  });

  const loadMoreEstates = async (page: number) => {
    setLoading(true);
    try {
      const estates =
        type === 'estate'
          ? await getEstates({
              query: {
                page,
                ...(filterOwn && { filterOwn }),
                ...(isNotEmptyFilters && { filters }),
                ...(sort &&
                  (sort.by || sort.order) && {
                    sort: {
                      ...(sort.by && { by: sort.by }),
                      ...(sort.order && { order: sort.order }),
                    },
                  }),
              },
            })
          : await getProjects({
              query: {
                page,
                ...(isNotEmptyFilters && { filters }),
              },
            });
      if (!estates.length) return setStopLoad(true);

      const estatesCollection = estates.reduce<EstatesState>((acc, v) => {
        acc[v.id] = v;
        return acc;
      }, {});
      setEstates((prev) =>
        prev
          ? {
              ...prev,
              ...estatesCollection,
            }
          : estatesCollection
      );
    } finally {
      setLoading(false);
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: ignored
  useEffect(() => {
    loadMoreEstates(pageNum);
  }, [pageNum]);

  // biome-ignore lint/correctness/useExhaustiveDependencies:ignored
  useEffect(() => {
    setEstates(null); // Reset the estates when filters or sorting change
    resetPageNum();
    setStopLoad(false);
    loadMoreEstates(pageNum);
  }, [filters, sort]);

  return (
    <Fragment>
      {estates &&
        Object.keys(estates).map((id) => {
          const estate = estates?.[id];
          const price = getFinalPrice(estate);
          const roomsCount = getFinalAreaRoom(estate);
          const livingArea = getFinalLivingArea(estate);

          return (
            <div key={estate.id} ref={lastElementRef}>
              <Card
                status={estate.status}
                id={estate.id}
                address={estate.address}
                livingArea={livingArea}
                name={estate.name}
                price={price}
                roomsCount={roomsCount}
                createdAt={estate.createdAt}
                creator={`${getRenderValue(estate.creator?.firstName)} ${getRenderValue(estate.creator?.lastName)}`}
                thumbnailId={estate.thumbnail?.mediaId || ''}
                propertyLevel={
                  'estateDetails' in estate ? PROPERTY_LEVELS.OBJECT : PROPERTY_LEVELS.PROJECT
                }
                {...cardProps}
              />
            </div>
          );
        })}
    </Fragment>
  );
};
